import React from 'react';

import PropTypes from 'prop-types';
import styled, { css } from 'styled-components';

import { HTMLAsset, ImageAsset } from '@techstyle/react-assets';
import { Desktop, Mobile } from '@techstyle/react-components-legacy';

import { mobile, desktop } from '../../styles';
import TemplateButtons from '../TemplateButtons/TemplateButtons';

/**
 * For larger viewports, ImageAsset does its thing.
 * For small viewports, the text should stack underneath the image, so the background-image styles need to be unset, but this only works if `maintainAspectRatio` is true.
 */
const unsetBgStyles = css`
  div[data-asset-html] {
    display: none;
  }
`;
const isHeaderStyles = css`
  display: flex;
  flex-direction: column;

  img {
    flex: none;
    order: 2;
  }
`;

const BackgroundImageAsset = styled(ImageAsset)`
  ${mobile`
    ${props => props.maintainAspectRatio && unsetBgStyles};
    ${props => props.isHeader && isHeaderStyles};
  `};

  flex: 0 0 100%;
  height: auto;
  position: relative;
  width: 100%;

  background-color: ${props => props.bgColor};
  background-position: ${props => `${props.bgX} ${props.bgY}`};
  background-repeat: no-repeat;
  background-size: ${props => props.bgSize};
`;

const TextWrapper = styled.div`
  align-items: center;
  display: flex;
  flex-direction: column;
  justify-content: center;
  margin-left: ${props => (props.alignRight ? 'auto' : 'initial')};
  padding: 2em 1em;
  width: 100%;

  ${desktop`
    height: 100%;
    padding: 1em;

    ${props =>
      !props.fullWidth &&
      `
      width: 50%;
      `};
  `};
`;

const TransparentHTMLAsset = styled(HTMLAsset)`
  background-color: transparent;
  text-align: center;
  word-break: break-word;
`;

export default class TemplateBackgroundImageAsset extends React.PureComponent {
  static propTypes = {
    asset: PropTypes.object.isRequired,
    className: PropTypes.string,
    hoverImage: PropTypes.bool,
  };

  renderText() {
    const { asset, hoverImage } = this.props;
    const {
      options: {
        customVars: { textPosition },
      },
    } = asset;

    if (hoverImage) {
      /**
       * If an hover image has been uploaded, it means there are two images side by side in the same row, so ignore the `textPosition` and set it to full width.
       */
      return (
        <TextWrapper fullWidth>
          <TransparentHTMLAsset asset={asset} />
          <TemplateButtons asset={asset} />
        </TextWrapper>
      );
    }

    switch (textPosition) {
      case 'left':
        return (
          <TextWrapper>
            <TransparentHTMLAsset asset={asset} />
            <TemplateButtons asset={asset} />
          </TextWrapper>
        );
      case 'right':
        return (
          <TextWrapper alignRight>
            <TransparentHTMLAsset asset={asset} />
            <TemplateButtons asset={asset} />
          </TextWrapper>
        );
      default:
        return (
          <TextWrapper fullWidth>
            <TransparentHTMLAsset asset={asset} />
            <TemplateButtons asset={asset} />
          </TextWrapper>
        );
    }
  }

  render() {
    const { asset, className, hoverImage } = this.props;
    const {
      options: {
        customVars: { isHeader, imagePosition, horizontalFocus, verticalFocus },
        ...options
      },
    } = asset;

    const imageFilename = hoverImage ? options.hoverImage : asset.imageFilename;

    if (!imageFilename) {
      return null;
    }

    const maintainAspectRatio = hoverImage
      ? false
      : imagePosition === 'backgroundFull';

    const text = this.renderText();

    return (
      <>
        <BackgroundImageAsset
          background
          maintainAspectRatio={maintainAspectRatio}
          aspectRatio={imagePosition === 'backgroundFull' ? 'auto' : null}
          bgColor={options.bgColor || 'transparent'}
          bgSize={imagePosition === 'backgroundContain' ? 'contain' : 'cover'}
          bgX={horizontalFocus || '50%'}
          bgY={verticalFocus || '50%'}
          className={className}
          isHeader={isHeader}
          asset={{ ...asset, imageFilename }}
          imageFilename={hoverImage ? options.hoverImage : asset.imageFilename}
        >
          <Desktop>{text}</Desktop>
        </BackgroundImageAsset>
        <Mobile>{text}</Mobile>
      </>
    );
  }
}
